/**
 * Панель AI-ассистента (US-10): боковая панель-диалог поверх текущего
 * экрана. Шапка → переключатель режимов → быстрые подсказки → лента
 * диалога → поле ввода (§«Дизайн» US-10).
 *
 * Контекст территории и года берётся с экрана (FR-1.3) и уходит в каждый
 * запрос; менять его пользователь продолжает штатными селекторами витрины —
 * панель не уводит со страницы.
 *
 * Данных «из головы» здесь нет: если `/api/assistant/*` недоступен,
 * в ленте появляется «сервис недоступен».
 */
import { Sparkles, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAssistant } from './AssistantContext';
import { ChatStream } from './ChatStream';
import { Composer } from './Composer';
import { ModeSwitcher } from './ModeSwitcher';
import { useAssistantModes } from './useAssistantModes';
import { SuggestionChips } from './SuggestionChips';
import { useAssistantChat } from './useAssistantChat';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '../ui/sheet';
import { useKato } from '../../contexts/KatoContext';
import { useYear } from '../../contexts/YearContext';
import { exportNote } from '../../requests/assistant.request';
import type {
  AssistantAnswer,
  AssistantMode,
  NoteFormat,
} from '../../types/assistant';

/** Расширение файла выгрузки совпадает с кодом формата (FR-5.2). */
const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const AssistantPanel = () => {
  const { t, i18n } = useTranslation();
  const { open, setOpen, closeAssistant } = useAssistant();
  const { selectedKato } = useKato();
  const { selectedYear } = useYear();
  const navigate = useNavigate();

  // Набор режимов приходит с бэка: он знает роль и знает, какие волны уже
  // поставлены (FR-2.3, Сценарий 7).
  const { modes } = useAssistantModes(
    selectedKato,
    i18n.resolvedLanguage ?? 'ru',
    open,
  );
  const [mode, setMode] = useState<AssistantMode>('data');
  const [exporting, setExporting] = useState<NoteFormat | null>(null);

  // Роль могла смениться (перелогин), а бэк — убрать режим: выбранным
  // остаётся только то, что реально доступно сейчас.
  const activeMode = modes.some((item) => item.id === mode) ? mode : 'data';
  const activeModeDef =
    modes.find((item) => item.id === activeMode) ?? modes[0] ?? null;

  const onModeDetected = useCallback((detected: AssistantMode) => {
    setMode(detected);
  }, []);

  const chat = useAssistantChat({
    kato: selectedKato,
    year: selectedYear,
    lang: i18n.resolvedLanguage ?? 'ru',
    onModeDetected,
  });

  const handlePrompt = (text: string, nextMode: AssistantMode | null) => {
    const target = nextMode ?? activeMode;
    if (nextMode !== null) setMode(nextMode);
    chat.send(text, target);
  };

  const handleNavigate = (href: string) => {
    closeAssistant();
    navigate(href);
  };

  const handleExport = (answer: AssistantAnswer, format: NoteFormat) => {
    // Выгрузка идёт по идентификатору ЗАПИСКИ, а не реплики: бэк хранит её
    // один раз, поэтому «⬇ Word» после «⬇ Excel» отдаёт тот же документ, а не
    // вторую генерацию модели (FR-5.2).
    if (answer.noteId === null) return;
    setExporting(format);
    exportNote(answer.noteId, format)
      .then((blob) => {
        downloadBlob(blob, `${t('assistant.note.fileName')}.${format}`);
      })
      .catch((error: unknown) => {
        console.error('exportNote failed', error);
        toast.error(t('assistant.errors.exportFailed'));
      })
      .finally(() => setExporting(null));
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        hideClose
        className="max-w-[29.5rem] p-0"
        data-testid="assistant-panel"
      >
        <header className="flex shrink-0 items-center gap-3 bg-[#0F1512] px-4 py-3.5 text-text-inverted">
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-card bg-brand-500"
            aria-hidden="true"
          >
            <Sparkles className="size-5" />
          </span>
          <div className="min-w-0">
            <SheetTitle className="truncate">{t('assistant.title')}</SheetTitle>
            <SheetDescription className="truncate text-white/50">
              {t('assistant.context', {
                kato: selectedKato ?? '—',
                year: selectedYear ?? t('header.yearAuto'),
              })}
            </SheetDescription>
          </div>
          <button
            type="button"
            onClick={closeAssistant}
            aria-label={t('common.close')}
            data-testid="assistant-close"
            className="ml-auto flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-white/20"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </header>

        <ModeSwitcher modes={modes} active={activeMode} onSelect={setMode} />
        <SuggestionChips
          mode={activeModeDef}
          disabled={chat.pending}
          onPick={(text) => chat.send(text, activeMode)}
        />
        <ChatStream
          entries={chat.entries}
          pending={chat.pending}
          onPrompt={handlePrompt}
          onNavigate={handleNavigate}
          onExport={handleExport}
          exporting={exporting}
        />
        <Composer
          disabled={chat.pending}
          onSend={(text) => chat.send(text, activeMode)}
        />
      </SheetContent>
    </Sheet>
  );
};
